import { createReducer, createActions } from 'reduxsauce'
import { AnyAction } from 'redux'

const PENDING_TIMEOUT_MS = 30000
const HISTORY_MAX = 50

export interface DiagnosticPing {
  id: string
  slaveId: number
  sentAt: number
  resolvedAt?: number
  kind?: 'response' | 'timeout'
  latencyMs?: number
}

export interface DiagnosticsState {
  pingsBySlave: Record<number, DiagnosticPing[]>
}

export const { Types, Creators } = createActions({
  diagnosticsCheckSent: ['payload'],
  diagnosticsResponseReceived: ['payload'],
  diagnosticsClearSlave: ['payload']
})

export default Creators

export const INITIAL_STATE: DiagnosticsState = {
  pingsBySlave: {}
}

type Reducer = (state: DiagnosticsState, action: AnyAction) => DiagnosticsState

const expirePending = (pings: DiagnosticPing[], now: number): DiagnosticPing[] =>
  pings.map(p => {
    if (p.resolvedAt != null) return p
    if (now - p.sentAt > PENDING_TIMEOUT_MS) {
      return { ...p, resolvedAt: p.sentAt + PENDING_TIMEOUT_MS, kind: 'timeout', latencyMs: PENDING_TIMEOUT_MS }
    }
    return p
  })

const trim = (pings: DiagnosticPing[]): DiagnosticPing[] =>
  pings.length > HISTORY_MAX ? pings.slice(pings.length - HISTORY_MAX) : pings

const checkSent: Reducer = (state, action) => {
  const { slaveId, id, sentAt } = action.payload
  const existing = state.pingsBySlave[slaveId] ?? []
  const next = trim([...expirePending(existing, sentAt), { id, slaveId, sentAt }])
  return { ...state, pingsBySlave: { ...state.pingsBySlave, [slaveId]: next } }
}

const resolveOldest = (state: DiagnosticsState, slaveId: number, receivedAt: number, kind: 'response' | 'timeout'): DiagnosticsState => {
  const existing = state.pingsBySlave[slaveId]
  if (existing == null || existing.length === 0) return state
  const expired = expirePending(existing, receivedAt)
  const idx = expired.findIndex(p => p.resolvedAt == null)
  if (idx < 0) {
    return { ...state, pingsBySlave: { ...state.pingsBySlave, [slaveId]: expired } }
  }
  const next = expired.slice()
  const target = next[idx]
  next[idx] = {
    ...target,
    resolvedAt: receivedAt,
    kind,
    latencyMs: receivedAt - target.sentAt
  }
  return { ...state, pingsBySlave: { ...state.pingsBySlave, [slaveId]: next } }
}

const responseReceived: Reducer = (state, action) => {
  const { slaveId, receivedAt } = action.payload
  if (slaveId == null) return state
  return resolveOldest(state, slaveId, receivedAt, 'response')
}

const wsTimeout: Reducer = (state, action) => {
  const slaveId = action.payload?.id
  if (slaveId == null) return state
  return resolveOldest(state, slaveId, Date.now(), 'timeout')
}

const wsStatusUpdate: Reducer = (state, action) => {
  const slaveId = action.payload?.id
  if (slaveId == null) return state
  const existing = state.pingsBySlave[slaveId]
  if (existing == null || !existing.some(p => p.resolvedAt == null)) return state
  return resolveOldest(state, slaveId, Date.now(), 'response')
}

const clearSlave: Reducer = (state, action) => {
  const slaveId = action.payload?.slaveId
  if (slaveId == null) return state
  const next = { ...state.pingsBySlave }
  delete next[slaveId]
  return { ...state, pingsBySlave: next }
}

export const reducer = createReducer(INITIAL_STATE, {
  [Types.DIAGNOSTICS_CHECK_SENT]: checkSent,
  [Types.DIAGNOSTICS_RESPONSE_RECEIVED]: responseReceived,
  [Types.DIAGNOSTICS_CLEAR_SLAVE]: clearSlave,
  WS_MESSAGE_TIMEOUT: wsTimeout,
  WS_MESSAGE_STATUS_UPDATE: wsStatusUpdate
})

export const selectSlavePings = (state: { diagnostics: DiagnosticsState }, slaveId: number): DiagnosticPing[] => {
  return state.diagnostics.pingsBySlave[slaveId] ?? []
}

export interface DiagnosticsStats {
  total: number
  responses: number
  timeouts: number
  pending: number
  successRate: number
  avgLatencyMs: number | null
  p95LatencyMs: number | null
  lastLatencyMs: number | null
}

export const computeStats = (pings: DiagnosticPing[]): DiagnosticsStats => {
  const resolved = pings.filter(p => p.resolvedAt != null)
  const responses = resolved.filter(p => p.kind === 'response')
  const timeouts = resolved.filter(p => p.kind === 'timeout')
  const pending = pings.length - resolved.length
  const latencies = responses
    .map(p => p.latencyMs)
    .filter((n): n is number => n != null)
    .sort((a, b) => a - b)
  const avg = latencies.length > 0
    ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
    : null
  const p95Idx = Math.max(0, Math.ceil(latencies.length * 0.95) - 1)
  const p95 = latencies.length > 0 ? latencies[p95Idx] : null
  const last = responses.length > 0 ? (responses[responses.length - 1].latencyMs ?? null) : null
  return {
    total: pings.length,
    responses: responses.length,
    timeouts: timeouts.length,
    pending,
    successRate: pings.length > 0 ? responses.length / (responses.length + timeouts.length || 1) : 0,
    avgLatencyMs: avg,
    p95LatencyMs: p95,
    lastLatencyMs: last
  }
}
